import axios from 'axios';
import { notifyToast } from './toastBridge';
import { requestRelogin } from './sessionExpiryBridge';
import { MAX_UPLOAD_BYTES, formDataFilesBytes, uploadTooLargeMessage } from './uploadLimits';
import { messaggioPermessoNegato } from './sediGestite';

// Ruolo dell'utente salvato al login, per adattare i messaggi d'errore.
const storedRole = (): string | undefined => {
    try {
        const raw = localStorage.getItem('authUser') || sessionStorage.getItem('authUser');
        return raw ? JSON.parse(raw)?.role : undefined;
    } catch {
        return undefined;
    }
};

// Permette a singole chiamate di disattivare il toast globale su 403/404/5xx
// (es. polling dove 404 significa "non ancora disponibile").
declare module 'axios' {
    export interface AxiosRequestConfig {
        skipGlobalErrorToast?: boolean;
    }
}

const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8080/api',
    headers: {
        'Content-Type': 'application/json',
    },
});

api.interceptors.request.use((config: any) => {
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    // Ogni richiesta si dichiara WEB: il BE lo usa per il kill-switch per client.
    config.headers['X-Client-Source'] = 'WEB';
    // Blocca prima dell'invio gli upload oltre il limite di Cloudflare (100 MB),
    // invece di caricare per minuti e ricevere un 413. Le prove grandi usano
    // presigned/chunk e non passano di qui.
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
        const bytes = formDataFilesBytes(config.data);
        if (bytes > MAX_UPLOAD_BYTES) {
            const msg = uploadTooLargeMessage(bytes);
            notifyToast(msg, 'error');
            const err: any = new Error(msg);
            err.config = config;
            err.isUploadTooLarge = true;
            err.response = { status: 413, data: { success: false, message: msg }, config };
            return Promise.reject(err);
        }
    }
    return config;
}, (error: any) => {
    return Promise.reject(error);
});

api.interceptors.response.use((response: any) => {
    // Rinnova automaticamente il token se il backend lo restituisce nell'header
    const renewedToken =
        response.headers['x-renewed-token'] ||
        response.headers['X-Renewed-Token'];

    if (renewedToken) {
        // Rinnova nel medesimo storage in cui era stato salvato
        if (localStorage.getItem('authToken')) {
            localStorage.setItem('authToken', renewedToken);
        } else {
            sessionStorage.setItem('authToken', renewedToken);
        }
    }

    return response;
}, async (error: any) => {
    const skipToast = error?.config?.skipGlobalErrorToast === true;

    if (error.response) {
        const status = error.response.status;

        if (status === 401) {
            const original = error.config || {};
            const isLoginCall = (original.url || '').includes('/auth/public/login');
            // 401 dal login stesso (credenziali errate): lo gestisce il chiamante,
            // niente logout né redirect.
            if (isLoginCall) {
                return Promise.reject(error);
            }

            const publicPaths = [
                '/login', '/invite/', '/notConfirmed/', '/notApproved',
                '/confirm/', '/unauthorized', '/privacy-policy',
                '/recover-password', '/recover', '/reset-password',
                '/terms-and-conditions'
            ];
            const isPublicPage = publicPaths.some(p => window.location.pathname.startsWith(p));

            // Invece del redirect, che smonterebbe l'app perdendo il lavoro non
            // salvato, apre la modale di re-login e poi ritenta la richiesta.
            // Se l'utente annulla o la modale non è montata, si fa il redirect.
            if (!isPublicPage && !original.__reloginRetry) {
                const newToken = await requestRelogin();
                if (newToken) {
                    original.__reloginRetry = true;
                    original.headers = original.headers || {};
                    original.headers.Authorization = `Bearer ${newToken}`;
                    return api(original);
                }
            }

            localStorage.removeItem('authToken');
            sessionStorage.removeItem('authToken');
            if (!isPublicPage) {
                window.location.href = '/login';
            }
            return Promise.reject(error);
        } else if (status === 413) {
            // 413 dal proxy (HTML, non JSON del BE), se il controllo pre-invio
            // non è riuscito a misurare il file.
            const msg = uploadTooLargeMessage();
            error.response.data = { success: false, message: msg };
            if (!skipToast) notifyToast(msg, 'error');
        } else if (!skipToast) {
            if (status === 403) {
                // Al responsabile un 403 arriva di solito da una sede che non gestisce.
                notifyToast(messaggioPermessoNegato(storedRole()), 'error');
            } else if (status === 404) {
                notifyToast('Risorsa non trovata.', 'error');
            } else if (status >= 500) {
                notifyToast('Errore del server. Riprova tra qualche istante.', 'error');
            }
        }
    } else if (error.request && !skipToast) {
        // Nessuna response: timeout, server irraggiungibile, offline, CORS bloccato.
        notifyToast('Connessione al server non riuscita. Verifica la rete.', 'error');
    }

    return Promise.reject(error);
});

export default api;